import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const philosophyPrinciples = [
    {
      title: 'Minimal but Effective Formulations',
      desc: 'We believe every ingredient inside a product should serve a purpose. Instead of adding unnecessary fillers or overwhelming formulations, we focus on balanced ingredient combinations that support healthy skin.',
    },
    {
      title: 'Science Before Marketing',
      desc: 'Every Metameni product is designed around scientifically studied active ingredients. We focus on formulations that help target real skin concerns such as acne, pigmentation, oily skin, dehydration, and skin barrier repair.',
    },
    {
      title: 'Honest Skincare',
      desc: 'We believe customers deserve transparency — clear ingredient communication, realistic expectations, no misleading claims, no fake “instant fairness” promises, and no unnecessary complexity.',
    },
  ];

  const brandPromises = [
    'Honest ingredient transparency',
    'No unrealistic promises',
    'No unnecessary fragrance overload',
    'No misleading marketing claims',
    'Only formulations backed by skincare science',
  ];

  return (
    <main className="min-h-screen">
      <section className="relative min-h-[70vh] flex items-center bg-black text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #cc7c66 0%, transparent 60%)' }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center py-24">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-[#cc7c66] mb-6">About Us</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light leading-tight">
              Honest skincare.
              <br />
              <em className="not-italic text-[#f5e6e0]">Built on science.</em>
            </h1>
          </div>
          <div className="text-gray-300 space-y-5 font-light leading-relaxed text-sm sm:text-base">
            <p>
              Metameni is a science-backed skincare brand created with a simple vision — to make
              skincare honest, effective, and easy to understand for everyone.
            </p>
            <p>
              In today’s skincare industry, people are often overwhelmed by unrealistic claims,
              complicated routines, and products filled with unnecessary ingredients. At Metameni, we
              wanted to change that.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-5 text-sm font-light leading-relaxed text-gray-600 sm:text-base">
          <p>
            We believe skincare should focus on what truly matters: effective ingredients,
            transparent formulations, and visible results.
          </p>
          <p>
            Our products are carefully designed for modern Indian skin concerns such as acne, oily
            skin, pigmentation, uneven skin tone, dark spots, dehydration, and barrier damage. Indian
            skin faces unique environmental challenges including pollution, humidity, heat, stress,
            and sun exposure, and our formulations are developed keeping these real conditions in
            mind.
          </p>
          <p>
            Every Metameni formula is built around clinically studied active ingredients such as
            Salicylic Acid, Niacinamide, Vitamin C, Hyaluronic Acid, and other scientifically backed
            compounds that are known to deliver results without unnecessary complexity.
          </p>
          <p>
            We believe that skincare does not need to be confusing. A good formulation should be
            minimal, purposeful, and effective. That is why we focus on creating products with
            carefully selected ingredients that support skin health without unnecessary fillers or
            marketing gimmicks.
          </p>
        </div>

        <div className="mt-16 rounded-2xl border border-gray-200 bg-[#fdf8f6] p-8 sm:p-10">
          <p className="mb-3 font-display text-3xl font-light text-black sm:text-4xl">
            “No Secrets. Only Science.”
          </p>
          <p className="mb-6 text-sm font-light text-gray-600">This means:</p>
          <ul className="space-y-2">
            {brandPromises.map((item) => (
              <li key={item} className="flex gap-2 text-sm font-light text-gray-600">
                <span className="text-[#cc7c66]">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-12 max-w-3xl mx-auto text-sm font-light leading-relaxed text-gray-600 sm:text-base">
          At Metameni, we are committed to building a skincare brand that customers can trust — not
          only for effective products but also for honest communication and education. We are not
          just creating skincare products. We are building a long-term skincare movement focused on
          simplicity, science, and skin confidence.
        </p>
        <p className="mt-6 max-w-3xl mx-auto font-display text-xl font-light text-black">
          Thank you for being a part of the Metameni journey.
          <br />
          <span className="text-gray-500 text-base">— Team Metameni</span>
        </p>
      </section>

      <section className="py-24 bg-[#fdf8f6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2 text-center">Our Philosophy</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light text-center mb-6">
            Simple, transparent, science-driven
          </h2>
          <p className="mx-auto max-w-3xl text-center text-sm font-light leading-relaxed text-gray-600 mb-16">
            The skincare industry is often filled with confusing routines, exaggerated marketing, and
            products that promise instant miracles. We believe real skincare should be based on
            consistency, education, and ingredients that are supported by research.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {philosophyPrinciples.map((principle, i) => (
              <div key={principle.title} className="bg-white border border-gray-200 p-8">
                <h3 className="font-display text-2xl font-light mt-2 mb-4 text-black">{principle.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed font-light">{principle.desc}</p>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-12 max-w-3xl text-center text-sm font-light leading-relaxed text-gray-600">
            Our goal is not to promote perfect skin. Our goal is to support healthier skin through
            consistency, education, and effective skincare solutions. Metameni is more than just a
            skincare brand — it is a commitment to honest skincare backed by science.
          </p>
        </div>
      </section>

      <section className="py-20 bg-black text-white text-center">
        <p className="text-xs tracking-[0.35em] uppercase text-[#cc7c66] mb-4">Start your journey</p>
        <h2 className="font-display text-5xl font-light mb-6">Ready to transform your skin?</h2>
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 border border-white/50 px-8 py-4 text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors"
        >
          Shop Now <ArrowRight size={14} />
        </Link>
      </section>
    </main>
  );
}
